import { Divider } from "@mui/material";
import { CloseSubMenuButton, NavigationPlaceNewOrderButton } from "./appNavigation.styles";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {
	CompressedMenuNavigationContainer,
	CompressedMenuPersonIcon,
	CompressedMenuSection,
	CompressedNavigationContainer,
	CompressedNavigationIcon,
	CompressedNavigationMenuButton,
	NavigationSubMenuItemButtonContainer,
	NavigationSubMenuItemText,
	NavigationSubMenuLinks,
	SubMenuListItemButton
} from "./compressedNavigation.styles";
import { useLocation } from "react-router";
import { models } from "types/api/viewModels.ts";
import { useContext } from "react";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { UserChannel, UserRole } from "types/declarations/enums.ts";

interface Props {
	handleClickNavigationOption: (navItem: models["UserNavigationItemViewModel"], index: number) => void;
	handleCloseSubMenu: () => void;
	mainNavigationItems: Array<models["UserNavigationItemViewModel"]>;
	subNavigation: models["UserNavigationItemViewModel"] | null;
	selectedIndex: number | undefined;
	userDetails?: models["UserViewModel"];
}

const CompressedNavigation = ({
	handleClickNavigationOption,
	handleCloseSubMenu,
	mainNavigationItems,
	subNavigation,
	selectedIndex,
	userDetails
}: Props) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { isOrderDetailsPage } = useUserLocation();

	const placeOrderId = "nav-orders-place-order";
	const truckOrdersId = "nav-orders-truck-orders";
	const customQuoteId = "nav-custom-quotes";
	const marketingMaterialsId = "nav-marketing-materials";

	const isRetail =
		userDetails?.userChannel === UserChannel.LOWES ||
		userDetails?.userChannel === UserChannel.HOME_DEPOT ||
		userDetails?.userChannel === UserChannel.MENARDS;
	const location = useLocation();

	const renderCompressedNavigationMenuIcon = (navItem: models["UserNavigationItemViewModel"], index: number) => {
		return (
			<CompressedNavigationMenuButton
				disableRipple
				data-id={navItem.id}
				id={navItem?.id ?? ""}
				data-testid={`${navItem.id}-link-compressed`}
				key={navItem.id}
				aria-describedby={`${navItem.id}-link-compressed`}
				onClick={() => handleClickNavigationOption(navItem, index)}
			>
				{selectedIndex === index ? (
					<img
						alt={navItem.title ?? ""}
						data-testid="main-menu-item-selected"
						src={"/assets/navigation_logos/" + navItem.selectedIcon}
					/>
				) : (
					<CompressedNavigationIcon src={"/assets/navigation_logos/" + navItem.icon} />
				)}
			</CompressedNavigationMenuButton>
		);
	};

	const renderCompressedSubNavigation = (navItem: models["UserNavigationItemViewModel"], index: number) => {
		const isCustomQuote = navItem.id === customQuoteId;
		let selectedTabToHighlight = navItem.link ? location.pathname.includes(navItem.link) : false;
		if (isOrderDetailsPage && navItem.link === "/projects") selectedTabToHighlight = true;

		if (isCustomQuote && !configurationsData?.featureFlags?.customQuoteEnabled) {
			return null;
		}

		if (navItem.id === marketingMaterialsId && isRetail) {
			return null;
		}

		if (
			navItem?.id === placeOrderId &&
			configurationsData?.featureFlags?.createOrdersEnabled &&
			(userDetails?.role !== UserRole.INTERNAL_DESIGNER || configurationsData.featureFlags.proQuoteEnabled)
		) {
			return (
				<NavigationPlaceNewOrderButton
					key={navItem.id}
					variant="contained"
					data-id="navigation-place-new-order-button"
					data-testid="navigation-place-new-order-button"
					fullWidth
					onClick={() => handleClickNavigationOption(navItem, index)}
				>
					{navItem.title}
				</NavigationPlaceNewOrderButton>
			);
		} else if (
			navItem.id !== placeOrderId &&
			!(navItem.id === truckOrdersId && !configurationsData?.featureFlags?.trucksEnabled)
		) {
			return (
				<NavigationSubMenuItemButtonContainer key={navItem.id}>
					<SubMenuListItemButton
						disableRipple
						data-id={navItem.id}
						id={navItem.id ?? ""}
						aria-describedby={`${navItem.id}-link-submenu`}
						data-testid={`navigation-subMenu-${navItem.id}`}
						onClick={() => handleClickNavigationOption(navItem, index)}
						selected={selectedTabToHighlight}
					>
						<NavigationSubMenuItemText>
							{navItem.title}
							{selectedTabToHighlight && (
								<img
									alt={navItem.title ?? ""}
									src="/assets/navigation_logos/SelectedNavItemBlueDot.svg"
								/>
							)}
						</NavigationSubMenuItemText>
					</SubMenuListItemButton>
				</NavigationSubMenuItemButtonContainer>
			);
		}
	};

	return (
		<CompressedNavigationContainer>
			<div>
				<CompressedMenuSection>
					<img
						src="/assets/tandem_logos/CWGLogoNoText.svg"
						alt="CWG Logo"
						data-testid="tandem-branding"
					/>
				</CompressedMenuSection>
				<Divider />
				<CompressedMenuSection>
					<CompressedMenuPersonIcon onClick={handleCloseSubMenu} />
				</CompressedMenuSection>
				<Divider />
				<CompressedMenuNavigationContainer>
					{mainNavigationItems.map((navItem, index) => renderCompressedNavigationMenuIcon(navItem, index))}
				</CompressedMenuNavigationContainer>
			</div>
			<div>
				<NavigationSubMenuLinks>
					<CloseSubMenuButton
						onClick={handleCloseSubMenu}
						data-id="navigation-close-sub-menu-button"
						data-testid="navigation-close-sub-menu-button"
					>
						<ChevronLeft />
						<span>{subNavigation?.title}</span>
					</CloseSubMenuButton>
					{subNavigation?.navigationItems?.map((navItem, index) =>
						renderCompressedSubNavigation(navItem, index)
					)}
				</NavigationSubMenuLinks>
			</div>
		</CompressedNavigationContainer>
	);
};

export default CompressedNavigation;
